import React, { useState } from "react";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Building2, 
  ChevronDown, 
  Search,
  Check,
  Plus
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ClientSelector = () => {
  const { clients, selectedClient, selectClient, loading } = useClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.short_name?.toLowerCase().includes(search.toLowerCase()) ||
    client.pan?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (client) => {
    selectClient(client);
    setOpen(false);
    setSearch("");
  };

  if (loading) {
    return (
      <div className="h-10 w-64 bg-slate-100 animate-pulse rounded-lg"></div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-64 justify-between bg-white border-slate-200 hover:bg-slate-50"
          data-testid="client-selector"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
            {selectedClient ? (
              <span className="truncate font-medium">
                {selectedClient.short_name || selectedClient.name}
              </span>
            ) : (
              <span className="text-slate-500">Select Client</span>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b">
          <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-md">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 bg-transparent h-8 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="client-search"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-auto p-1">
          {filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <button
                key={client.id}
                onClick={() => handleSelect(client)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-md hover:bg-slate-100 transition-colors ${
                  selectedClient?.id === client.id ? "bg-slate-100" : ""
                }`}
                data-testid={`client-option-${client.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{client.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {client.pan || "No PAN"} {client.short_name && `• ${client.short_name}`}
                  </p>
                </div>
                {selectedClient?.id === client.id && (
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-2" />
                )}
              </button>
            ))
          ) : (
            <div className="px-3 py-6 text-center text-slate-500 text-sm">
              {search ? "No clients found" : "No clients yet"}
            </div>
          )}
        </div>
        <div className="p-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              setOpen(false);
              navigate("/clients");
            }}
            data-testid="manage-clients-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Manage Clients
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ClientSelector;
